import { useState, useEffect, useCallback } from 'react';
import {
  Smartphone, Loader2, CalendarDays, Clock, Check, Eye, EyeOff, Pencil,
  Play, X, ExternalLink,
} from 'lucide-react';
import VideoModal from '@/components/common/VideoModal';

const API = process.env.REACT_APP_BACKEND_URL;

const ShortsSection = ({ headers }) => {
  const [shorts, setShorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ status: 'draft', publish_at: '' });
  const [previewShort, setPreviewShort] = useState(null);

  const fetchShorts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/shorts`, { headers, credentials: 'include' });
      if (res.ok) { const data = await res.json(); setShorts(data.shorts || []); }
    } catch (e) { console.error('Failed to fetch shorts:', e); } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchShorts(); }, [fetchShorts]);

  const updateShort = async (videoId, patch) => {
    setSavingId(videoId);
    try {
      const res = await fetch(`${API}/api/admin/shorts/${videoId}`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(patch),
      });
      if (res.ok) {
        const data = await res.json();
        setShorts(arr => arr.map(s => s.video_id === videoId ? { ...s, ...data.short } : s));
      }
    } catch (e) { console.error('Failed to update short:', e); } finally { setSavingId(null); }
  };

  // Quick toggle published ↔ draft — mirrors VideoTab's Eye/EyeOff button
  const toggleStatus = async (s) => {
    const newStatus = s.status === 'published' ? 'draft' : 'published';
    await updateShort(s.video_id, { status: newStatus, publish_at: null });
  };

  const startEdit = (s) => {
    setEditingId(s.video_id);
    setEditForm({ status: s.status || 'draft', publish_at: s.publish_at || '' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ status: 'draft', publish_at: '' });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const patch = { status: editForm.status };
    if (editForm.status === 'scheduled') {
      if (!editForm.publish_at) return; // require a date
      patch.publish_at = editForm.publish_at;
    } else {
      patch.publish_at = null;
    }
    await updateShort(editingId, patch);
    cancelEdit();
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-slate-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading shorts…
      </div>
    );
  }

  const publishedCount = shorts.filter(s => s.status === 'published').length;

  return (
    <div className="mt-8 pt-8 border-t border-slate-200" data-testid="admin-shorts-section">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-pink-500" />
          <h3 className="text-lg font-bold text-taxera-charcoal">Shorts (9:16 Social)</h3>
        </div>
        <span className="text-xs text-slate-400">
          {shorts.length} shorts — {publishedCount} live
        </span>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Vertical 9:16 clips. Drafts are hidden from the public Films page Shorts section.
      </p>

      <div className="space-y-3">
        {shorts.map((s, i) => {
          const isEditing = editingId === s.video_id;
          const saving = savingId === s.video_id;
          const status = s.status || 'draft';

          return (
            <div key={s.video_id} data-testid={`short-row-wrapper-${s.video_id}`}>
              <div
                className={`flex items-center gap-4 bg-white border p-4 transition-all hover:shadow-sm ${
                  isEditing ? 'rounded-t-xl border-b-0' : 'rounded-xl'
                } ${
                  status === 'draft' ? 'border-amber-200 bg-amber-50/30' :
                  status === 'scheduled' ? 'border-blue-200 bg-blue-50/30' :
                  status === 'archived' ? 'border-slate-300 bg-slate-50/50 opacity-60' :
                  'border-slate-200'
                }`}
                data-testid={`short-row-${s.video_id}`}
              >
                {/* Index */}
                <div className="flex items-center gap-1 text-slate-300">
                  <span className="text-xs font-mono w-5 text-center">{i + 1}</span>
                </div>

                {/* Category-style label (always "9:16") */}
                <div className="flex items-center gap-2 w-28 shrink-0">
                  <div className="w-3 h-3 rounded-full bg-pink-500" />
                  <span className="text-2xs font-bold uppercase tracking-wide text-slate-400">9:16</span>
                </div>

                {/* Title + status */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-taxera-charcoal truncate">{s.video_id}</span>
                    {status === 'draft' && (
                      <span className="text-2xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-semibold">DRAFT</span>
                    )}
                    {status === 'scheduled' && (
                      <span className="text-2xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-semibold flex items-center gap-1">
                        <Clock className="w-3 h-3" /> SCHEDULED
                      </span>
                    )}
                    {status === 'archived' && (
                      <span className="text-2xs px-2 py-0.5 bg-slate-200 text-slate-500 rounded-full font-semibold">ARCHIVED</span>
                    )}
                    {status === 'published' && (
                      <span className="text-2xs px-2 py-0.5 bg-green-50 text-green-600 rounded-full font-semibold">LIVE</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 truncate mt-0.5">
                    Vertical 9:16 clip — perfect for LinkedIn, Instagram, TikTok
                  </p>
                  {status === 'scheduled' && s.publish_at && (
                    <p className="text-2xs text-blue-500 mt-0.5 flex items-center gap-1">
                      <CalendarDays className="w-3 h-3" />
                      Publishes {new Date(s.publish_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                  )}
                </div>

                {/* Size */}
                <span className="text-xs font-mono text-slate-400 w-12 text-center shrink-0">{s.size_mb}MB</span>

                {/* Actions — same icon style as VideoTab */}
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => setPreviewShort(s)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-taxera-blue hover:bg-taxera-blue/10 transition-colors"
                    title="Play preview"
                    data-testid={`short-preview-${s.video_id}`}
                  >
                    <Play className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => toggleStatus(s)}
                    disabled={saving}
                    className={`p-1.5 rounded-lg transition-colors ${
                      status === 'published'
                        ? 'text-green-500 bg-green-50'
                        : 'text-slate-300 hover:text-green-500 hover:bg-green-50'
                    } disabled:opacity-50`}
                    title={status === 'published' ? 'Unpublish (set as draft)' : 'Publish'}
                    data-testid={`short-toggle-${s.video_id}`}
                  >
                    {status === 'published' ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  </button>
                  <button
                    onClick={() => isEditing ? cancelEdit() : startEdit(s)}
                    className={`p-1.5 rounded-lg transition-colors ${
                      isEditing ? 'text-taxera-blue bg-taxera-blue/10' : 'text-slate-400 hover:text-taxera-blue hover:bg-taxera-blue/10'
                    }`}
                    title={isEditing ? 'Close editor' : 'Edit status & schedule'}
                    data-testid={`short-edit-${s.video_id}`}
                  >
                    {isEditing ? <X className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
                  </button>
                  <a
                    href={`${API}${s.download_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded-lg text-slate-400 hover:text-taxera-blue hover:bg-taxera-blue/10 transition-colors"
                    title="Open file"
                    data-testid={`short-open-${s.video_id}`}
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>

              {/* Inline expand — same pattern as VideoTab */}
              {isEditing && (
                <div className="bg-white border border-t-0 border-slate-200 rounded-b-xl p-6" data-testid={`short-form-inline-${s.video_id}`}>
                  <h3 className="text-sm font-bold text-taxera-charcoal mb-4 flex items-center gap-2">
                    <Pencil className="w-3.5 h-3.5 text-taxera-blue" /> Editing: {s.video_id}
                  </h3>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
                      <select
                        value={editForm.status}
                        onChange={(e) => setEditForm(f => ({
                          ...f,
                          status: e.target.value,
                          ...(e.target.value !== 'scheduled' ? { publish_at: '' } : {}),
                        }))}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                        data-testid={`short-form-status-${s.video_id}`}
                      >
                        <option value="draft">Draft</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="published">Published (now)</option>
                        <option value="archived">Archived</option>
                      </select>
                    </div>
                    {editForm.status === 'scheduled' && (
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center gap-1">
                          <CalendarDays className="w-3.5 h-3.5" /> Publish Date & Time
                        </label>
                        <input
                          type="datetime-local"
                          value={editForm.publish_at ? editForm.publish_at.slice(0, 16) : ''}
                          onChange={(e) => setEditForm(f => ({
                            ...f,
                            publish_at: e.target.value ? new Date(e.target.value).toISOString() : '',
                          }))}
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                          data-testid={`short-form-publish-at-${s.video_id}`}
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={saveEdit}
                      disabled={saving || (editForm.status === 'scheduled' && !editForm.publish_at)}
                      className="inline-flex items-center gap-2 px-5 py-2 bg-taxera-blue text-white rounded-lg text-sm font-medium hover:bg-taxera-blue/90 disabled:opacity-50 transition-colors"
                      data-testid={`short-form-save-${s.video_id}`}
                    >
                      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      Update Short
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="px-5 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-200 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Video preview modal for shorts */}
      <VideoModal
        isOpen={!!previewShort}
        onClose={() => setPreviewShort(null)}
        video={previewShort ? {
          title: previewShort.video_id,
          description: `9:16 vertical short · ${previewShort.size_mb} MB`,
          duration: '',
          src: `${API}${previewShort.download_url}`,
        } : null}
      />
    </div>
  );
};

export default ShortsSection;
